/**
 * Onboarding Step 1: Business Activity
 *
 * Collects business_activity description (10-500 chars)
 */

import { type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

interface Step1BusinessProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

export function Step1Business({ value, onChange, error }: Step1BusinessProps) {
  const { t } = useTranslation(["onboarding", "validation"]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const charCount = value.length;
  const isValid = charCount >= 10 && charCount <= 500;

  return (
    <div className="space-y-5">
      <div>
        <label
          htmlFor="business-activity"
          className="mb-2 block text-sm font-semibold text-[var(--foreground)]"
        >
          {t("onboarding:step1.label")}
        </label>
        <p className="mb-3 text-sm leading-6 text-[var(--muted-foreground)]">
          {t("onboarding:step1.hint")}
        </p>
        <textarea
          id="business-activity"
          value={value}
          onChange={handleChange}
          rows={6}
          maxLength={500}
          className={`w-full rounded-[var(--r-sm)] border bg-[var(--background)] px-4 py-4 text-sm leading-6 text-[var(--foreground)] shadow-sm transition focus:outline-none focus:ring-4 ${
            error
              ? "border-[color:color-mix(in_srgb,var(--destructive)_35%,transparent)] focus:border-[var(--destructive)] focus:ring-[color:color-mix(in_srgb,var(--destructive)_20%,transparent)]"
              : "border-[var(--input)] focus:border-[var(--ring)] focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]"
          }`}
          aria-label={t("onboarding:step1.label")}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "business-activity-error" : undefined}
        />
        <div className="mt-3 flex items-start justify-between gap-4">
          <div>
            {error && (
              <p
                id="business-activity-error"
                role="alert"
                className="text-xs font-medium text-[var(--destructive)]"
              >
                {error}
              </p>
            )}
            {!error && charCount > 0 && !isValid && charCount < 10 && (
              <p className="text-xs font-medium text-[var(--risk-medium)]">
                {t("validation:company.businessActivityMinLength", {
                  count: 10,
                })}
              </p>
            )}
          </div>
          <p className="text-xs font-medium text-[var(--muted-foreground)]">
            {t("onboarding:step1.charCount", {
              remaining: 500 - charCount,
              max: 500,
            })}
          </p>
        </div>
      </div>
    </div>
  );
}
